import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Stack, Button, Typography } from "@altimateai/lego";
import { RelativeComponentLoader } from "@/components/loader";
import { getSharedSession } from "@/helpers/apis";
import { IconLock, IconAlertCircle, IconHome } from "@tabler/icons-react";
import {
  ChatContext,
  LoadingState,
  MessageList,
  processPlaygroundSessionTurns,
} from "@altimateai/ui-components/chatbotV2";

const SharedPlayground = () => {
  const { share_id } = useParams<{ share_id: string }>();

  const {
    data: sharedSession,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["shared-session", share_id],
    queryFn: () => getSharedSession(share_id!),
    enabled: !!share_id,
    retry: false,
  });

  // Process turns into the format expected by ChatMessages
  const messages = useMemo(() => {
    if (!sharedSession?.turns) return [];

    // Process turns similar to how Playground does it
    try {
      // Zod chatBlockSchema validates turns; cast needed because the Zod schema's
      // type enum is wider than the TypeScript AgentStreamResponse type.
      const processed = processPlaygroundSessionTurns(
        sharedSession.turns as Parameters<typeof processPlaygroundSessionTurns>[0],
        sharedSession.session_id,
        [],
        {},
        "completed"
      );
      // Get the session and return its messages
      const session = processed[sharedSession.session_id];
      return session?.messages || [];
    } catch {
      return [];
    }
  }, [sharedSession]);

  // Error states — the axios interceptor rejects with the detail string from the backend
  if (error) {
    const errorMessage = typeof error === "string" ? error : ((error as Error)?.message ?? "");
    const isExpired = errorMessage === "expired";
    const isUnauthorized =
      errorMessage === "unauthorized" || errorMessage.includes("Authentication required");

    return (
      <Stack
        direction="column"
        align="center"
        justify="center"
        className="al-h-[80vh] al-text-center al-px-4"
      >
        {isUnauthorized ? (
          <>
            <IconLock className="al-h-16 al-w-16 al-text-muted-foreground al-mb-4" />
            <Typography variant="h3" className="al-mb-2">
              Access Denied
            </Typography>
            <Typography variant="body" className="al-text-muted-foreground al-mb-6">
              You don&apos;t have permission to view this shared conversation.
            </Typography>
          </>
        ) : isExpired ? (
          <>
            <IconAlertCircle className="al-h-16 al-w-16 al-text-muted-foreground al-mb-4" />
            <Typography variant="h3" className="al-mb-2">
              Link Expired
            </Typography>
            <Typography variant="body" className="al-text-muted-foreground al-mb-6">
              This shared link has expired and is no longer available.
            </Typography>
          </>
        ) : (
          <>
            <IconAlertCircle className="al-h-16 al-w-16 al-text-destructive al-mb-4" />
            <Typography variant="h3" className="al-mb-2">
              Error Loading Conversation
            </Typography>
            <Typography variant="body" className="al-text-muted-foreground al-mb-6">
              Failed to load shared conversation
            </Typography>
          </>
        )}
        <Button asChild>
          <Link to="/">
            <IconHome className="al-mr-2 al-h-4 al-w-4" />
            Go Home
          </Link>
        </Button>
      </Stack>
    );
  }

  if (isLoading) {
    return (
      <Stack direction="column" align="center" justify="center" className="al-h-[80vh]">
        <RelativeComponentLoader label="Loading shared conversation..." />
      </Stack>
    );
  }

  if (!sharedSession) {
    return (
      <Stack
        direction="column"
        align="center"
        justify="center"
        className="al-h-[80vh] al-text-center"
      >
        <IconAlertCircle className="al-h-16 al-w-16 al-text-muted-foreground al-mb-4" />
        <Typography variant="h3" className="al-mb-2">
          Conversation Not Found
        </Typography>
        <Typography variant="body" className="al-text-muted-foreground al-mb-6">
          This shared conversation could not be found.
        </Typography>
        <Button asChild>
          <Link to="/">
            <IconHome className="al-mr-2 al-h-4 al-w-4" />
            Go Home
          </Link>
        </Button>
      </Stack>
    );
  }

  return (
    <div className="studio-page al-h-screen al-overflow-y-auto al-bg-[#F1F5F9]">
      {/* Header */}
      <div className="al-border-b al-bg-white al-px-6 al-py-4">
        <Stack direction="row" align="center" justify="between">
          <Stack direction="column" gap="xs">
            <Typography variant="h4" className="al-line-clamp-1">
              {sharedSession.share_title || sharedSession.session_name}
            </Typography>
            <Typography variant="body" className="al-text-muted-foreground al-text-sm">
              Shared conversation (read-only)
            </Typography>
          </Stack>
          <Button variant="outline" asChild>
            <Link to="/studio">Open Studio</Link>
          </Button>
        </Stack>
      </div>

      {/* Conversation */}
      <div className="al-max-w-4xl al-mx-auto al-py-6 al-px-4">
        {messages.length > 0 ? (
          <div className="al-bg-white al-rounded-lg al-shadow-sm al-p-6">
            <ChatContext.Provider
              value={{
                sessions: {},
                currentSessionId: null,
                loadingState: LoadingState.UNINITIALIZED,
                error: null,
                abortController: null,
                currentActions: [],
                currentTodos: [],
                dispatch: () => {},
              }}
            >
              <MessageList messages={messages} />
            </ChatContext.Provider>
          </div>
        ) : (
          <Stack direction="column" align="center" justify="center" className="al-py-12">
            <Typography variant="body" className="al-text-muted-foreground">
              This conversation has no messages.
            </Typography>
          </Stack>
        )}
      </div>
    </div>
  );
};

export default SharedPlayground;
